import get from 'lodash/get'
import {
  sessionConfig,
  specificRoles,
  configDocuments,
  collections,
  customer_kinds,
} from '@common/constants'
import config from '@common/config.json'
import { isKeycloakTokenExpiredSafe } from '@utils/keycloakUtils'

const hubUserTransmissibleIdLocalStorageKey = 'hub_user_transmissible_id'
const hubUserTokensLocalStorageKey = 'hub_user_tokens'
const hubUserLocalStorageKey = 'hub_user'

const state = {
  currentUser: null,
  currentHubUser: null,
  currentHubUserTransmissibleId: null,
  currentHubUserTokens: null,
  currentCustomer: null,
  customers: [],
  isOmniscientAdmin: false,
  currentPermissions: null,
  currentDefaultPage: null,
}

const getters = {
  currentUser: (state) => state.currentUser,
  // TOFIX: sometimes store is not loaded with hub user because user reloads a URL without the hub transmissible id, so we check local storage
  // => we would prefer not having a state but localStorage is not reactive and does not trigger vue watchers ...
  hubUserTransmissibleId: () => {
    return (
      state.currentHubUserTransmissibleId ??
      localStorage.getItem(hubUserTransmissibleIdLocalStorageKey)
    )
  },
  hubUserTokens: () => {
    if (state.currentHubUserTokens) {
      return state.currentHubUserTokens
    }

    let foundTokens = null
    try {
      foundTokens = JSON.parse(
        localStorage.getItem(hubUserTokensLocalStorageKey),
      )
    } catch (parseError) {
      console.debug(
        '[auth.hubUserTokens] unable to parse localStorage for hub_user_tokens key.',
      )
    }

    console.log('[auth.hubUserTokens] foundTokens:', foundTokens)

    return foundTokens
  },
  hubUser: () => {
    // when set in state, return last known current hub user
    if (state.currentHubUser) {
      return state.currentHubUser
    }

    // else try to search in localStorage: if hubUser was not fetched, it may still be existing in storage
    let foundUser = null
    try {
      foundUser = JSON.parse(localStorage.getItem(hubUserLocalStorageKey))
    } catch (parseError) {
      console.debug(
        '[auth.hubUser] unable to parse localStorage for hub_user key.',
      )
    }

    return foundUser
  },
  customers: (state) => state.customers,
  currentCustomer: (state) => state.currentCustomer,
  currentIndex: (state) => get(state.currentCustomer, 'index', ''),
  currentCustomerName: (state) => get(state.currentCustomer, 'name', null),
  currentProfile: (state) =>
    state.isOmniscientAdmin
      ? 'admin'
      : // the assumption is that in normal use cases, only 1 profile can exist for a given customer
        get(state.currentCustomer, 'profiles[0]', null),
  isAdmin: (state) =>
    state.isOmniscientAdmin
      ? true
      : state.currentCustomer?.profiles?.includes(specificRoles.admin),
  isSuperAdmin: (state) => Boolean(state.isOmniscientAdmin),
  isPreperer: (state) =>
    state.isOmniscientAdmin
      ? false
      : state.currentCustomer?.profiles?.includes(specificRoles.preperer),
  isWorker: (state) =>
    state.isOmniscientAdmin
      ? false
      : state.currentCustomer?.profiles?.includes(specificRoles.worker),
  isCustomerRenter: (state) =>
    get(state.currentCustomer, 'kind', false) === customer_kinds.RENTER,
  isCustomerWorksite: (state) =>
    get(state.currentCustomer, 'kind', false) === customer_kinds.WORKSITE,
  currentPermissions: (state) => state.currentPermissions,
  currentDefaultPage: (state) => state.currentDefaultPage,
}

const actions = {
  // TODO: probably not useful anymore, this has been moved to main.js in order to init keycloak before vue router init
  INIT_KEYCLOAK: async ({}, keycloak) => {
    let authResult = false
    try {
      authResult = await keycloak.init({
        onLoad: 'check-sso',
        // https://github.com/keycloak/keycloak/issues/14742
        // https://www.keycloak.org/docs/latest/securing_apps/#_javascript_adapter
        // responseMode: 'query',
        // not supported by modern browsers: https://www.keycloak.org/docs/latest/securing_apps/#_modern_browsers
        checkLoginIframe: false,
        // not supported by modern browsers: https://www.keycloak.org/docs/latest/securing_apps/#_modern_browsers
        // silentCheckSsoRedirectUri:
        // window.location.origin + '/silent-check-sso.html',
        // TODO: debug mode, remove once in prod
        enableLogging: true,
      })

      // TODO: init may trigger a navigation ?? to check
      console.debug('[auth/INIT_KEYLCOAK] init Keycloak result:', authResult)
    } catch (keycloakInitError) {
      console.error(
        '[auth/INIT_KEYLCOAK] init Keycloak error:',
        keycloakInitError,
      )
    }

    return authResult
  },
  CHECK_KEYCLOAK_TOKEN: async ({ dispatch }, { keycloak, backendName }) => {
    try {
      // TODO: when might this happen that init is not finished ? document
      // HACK: internal implem, keycloak can initialize only once
      if (!keycloak.didInitialize) {
        const authResult = await dispatch('INIT_KEYCLOAK', keycloak)

        if (!authResult) {
          console.warn(
            '[auth/CHECK_KEYCLOAK_TOKEN] failed to authenticate to KC during init.',
          )
          // skip next steps
          return false
        }
      }

      // When coming from hub, the local keycloak client does not download necessary cookies (they are not provided by the hub API) and
      // we force internal state by setting keycloak-js local variables manually. As a consequence, when refreshing the page,
      // we loose automatic check of keycloak-js auth state, and it is considered unauthenticated even though the tokens may still be valid.
      // That's why we have to handle that case in a dedicated if clause to try & refresh internal state properly.
      if (
        !keycloak.authenticated &&
        getters.hubUserTransmissibleId() &&
        getters.hubUserTokens()
      ) {
        console.debug(
          '[auth/CHECK_KEYCLOAK_TOKEN] keycloak client is not anthenticated but hubUser found in localStorage, trying to refresh token...',
        )

        const hubTransmissibleId = getters.hubUserTransmissibleId()

        const checkHubUserLoggedIn = await fetch(
          `${config.ubyHub[backendName].authApiBaseURL}/${hubTransmissibleId}/auth`,
          { method: 'GET' },
        )

        if (!checkHubUserLoggedIn.ok) {
          console.error(
            '[auth/CHECK_KEYCLOAK_TOKEN] failure trying to fetch hub user from API, returning false.',
          )
          return false
        }

        const hubUserTransmissibleId = getters.hubUserTransmissibleId()
        const { accessToken, refreshToken, tokenHub } = getters.hubUserTokens()

        const authResult = await dispatch('KEYCLOAK_REFRESH_TOKEN_HUB_USER', {
          keycloak,
          refreshToken,
          tokenHub,
          accessToken,
          hubUserTransmissibleId,
        })

        if (authResult) {
          console.debug(
            '[auth/CHECK_KEYCLOAK_TOKEN] KC auth success for hub user, redirecting to app...',
          )
          return true
        }

        // auth failed one way or another, return false to let calling method handle failure & logout accordingly
        return false
      }

      if (!keycloak || !keycloak?.authenticated) {
        console.warn(
          '[auth/CHECK_KEYCLOAK_TOKEN] keycloak user not authenticated.',
          keycloak?.authenticated,
        )

        // skip next steps
        return false
      }

      // try refresh token if only expired
      if (isKeycloakTokenExpiredSafe(keycloak)) {
        console.debug(
          '[auth/CHECK_KEYCLOAK_TOKEN] token is expired, trying to refresh...',
        )

        const tokenRefreshed = await dispatch(
          'REFRESH_KEYCLOAK_TOKEN',
          keycloak,
        )

        if (!tokenRefreshed) {
          console.error(
            '[auth/CHECK_KEYCLOAK_TOKEN] failure refreshing KC token.',
          )

          // skip next steps
          return false
        }
      }
    } catch (checkKeycloakTokenError) {
      console.error(
        '[auth/CHECK_TOKEN] error when trying to check keycloak token validity, returning false.',
        checkKeycloakTokenError,
      )
      return false
    }

    // else it is okay
    console.debug('[auth/CHECK_KEYCLOAK_TOKEN] keycloak user state is valid')

    return true
  },
  REFRESH_KEYCLOAK_TOKEN: async ({}, keycloak) => {
    console.debug('[auth/REFRESH_KEYCLOAK_TOKEN] triggered...')
    try {
      // TODO: handle minValidity period to refresh token when inside a short window of remaining lifespan
      // https://stackoverflow.com/a/74206583/801307
      // trigger logout from kuzzle with KC strategy & cleanup local stores
      const tokenRefreshed = await keycloak.updateToken()

      console.debug('[auth/REFRESH_KEYCLOAK_TOKEN] result:', tokenRefreshed)

      return tokenRefreshed
    } catch (tokenRefreshError) {
      console.error(
        `[auth/REFRESH_KEYCLOAK_TOKEN][error] failure trying to refresh token in keycloak.`,
        tokenRefreshError,
      )

      return false
    }
  },
  CHECK_KUZZLE_TOKEN: async ({ dispatch, getters }, kuzzle) => {
    console.debug('[auth/CHECK_KUZZLE_TOKEN] called.')
    try {
      // this is only to validate internal kuzzle token
      // TODO: what does it mean when using keycloak provider ? is the token revoked when KC session is revoked ?
      const result = await kuzzle.auth.checkToken()

      console.debug('[auth/CHECK_KUZZLE_TOKEN] token response:', result)

      if (result.kuid === '-1' || !result.valid) {
        console.warn(
          '[auth/CHECK_KUZZLE_TOKEN] internal kuzzle token invalid.',
          result,
        )

        //TODO: try trigger auto-refresh before rejecting ? or let router handle kuzzle re-login ?
        // this might not be the same behavior on kuzzle side, to check & test with logs ...
        return false
      }

      if (!getters.currentUser) {
        //TODO: handle if dispatch doesn't succeed ?
        await dispatch('FETCH_CURRENT_USER', kuzzle)
      }

      return true
    } catch (checkTokenError) {
      console.error(
        '[auth/CHECK_KUZZLE_TOKEN] unexpected error during token validation.',
        checkTokenError,
      )
      return false
    }
  },
  KEYCLOAK_LOGIN: async ({}, { keycloak, redirectUri, updatePassword }) => {
    try {
      let loginResult
      if (updatePassword) {
        console.debug(
          '[auth/KEYCLOAK_LOGIN] triggering password update request on login ...',
        )
        // https://www.keycloak.org/docs/latest/securing_apps/#api-reference
        loginResult = await keycloak.login({
          redirectUri,
          action: 'UPDATE_PASSWORD',
        })
      } else {
        loginResult = await keycloak.login({ redirectUri })
      }

      console.debug('[auth/KEYCLOAK_LOGIN] success:', loginResult)
      return true
    } catch (keycloakLoginError) {
      console.error(
        '[auth/KEYCLOAK_LOGIN] error during KC login:',
        keycloakLoginError,
      )
      return false
    }
  },
  KUZZLE_LOGIN: async ({ dispatch }, { kuzzle, strategy, keycloak }) => {
    let jwt
    try {
      // TODO : provide expires_in to handle proper token expiration ? or is it only KC expires ?
      // TODO : use kuzzle.auth controller directly ? will throw instead of return status failure ?
      const response = await kuzzle.query(
        {
          controller: 'auth',
          action: 'login',
          strategy: strategy,
          cookieAuth: true,
          body: {
            access_token: keycloak.token,
            id_token: keycloak.idToken,
            refresh_token: keycloak.refreshToken,
          },
        },
        { verb: 'POST' },
      )

      if (response.status !== 200) {
        throw response.error?.message
          ? new Error(response.error.message)
          : response.error ?? response.result
      }

      console.debug('[auth/KUZZLE_LOGIN] response result', response.result)

      // TODO: this does not work with cookie auth ??? jwt is null in response ...
      jwt = response.result.jwt
      kuzzle.jwt = jwt
      await dispatch('FETCH_CURRENT_USER', kuzzle)
    } catch (error) {
      console.warn(
        `[auth/KUZZLE_LOGIN] error trying to login to kuzzle & fetch current user for user with tokenHub '${keycloak?.tokenHub}'.`,
      )

      throw error
    }
  },
  KEYCLOAK_LOG_OUT: async ({}, { keycloak }) => {
    console.debug(
      '[auth/KEYCLOAK_LOG_OUT] called. cleaning up local KC token & auth state & redirect to KC logout page...',
    )

    try {
      await keycloak?.clearToken()
    } catch (e) {
      console.error(
        '[auth/KEYCLOAK_LOG_OUT] error while trying to clear local KC tokens:',
        e,
      )
    }
  },
  KUZZLE_LOG_OUT: async ({ commit }, { kuzzle, preserveKuzzleConnection }) => {
    console.debug(
      '[auth/KUZZLE_LOG_OUT] called. cleaning up local kuzzle session state & tokens...',
    )
    /**
     * You should tear down your session here.
     */
    commit('UNSET_CURRENT_USER')
    commit('UNSET_CURRENT_HUB_USER')
    commit('UNSET_CURRENT_CUSTOMER')
    commit('UNSET_CUSTOMERS_LIST')
    commit('site/SET_CURRENT_SITE', null, { root: true })
    commit('referential/SET_CURRENT_REFERENTIAL', null, { root: true })
    commit('customerConfig/SET_CURRENT_CUSTOMER_CONFIG', null, { root: true })
    commit('userPreferences/SET_USER_PREFERENCES', null, { root: true })

    try {
      await kuzzle.auth.logout()
      // WARN if KUZZLE_LOG_OUT is called before trying to connect another way programmatically,
      // this will fail because we have disconnected from kuzzle server.
      // use a flag in that case ? (see hub use case)
      if (!preserveKuzzleConnection) {
        console.debug(
          '[auth/KUZZLE_LOG_OUT] called. disconnecting from kuzzle server...',
        )
        await kuzzle.disconnect()
      }
      kuzzle.jwt = null
    } catch (e) {
      console.error(
        '[auth/KUZZLE_LOG_OUT] error while trying to log out of kuzzle & disconnecting from kuzzle server:',
        e,
      )
    }
    localStorage.removeItem('current_customer_name')
  },
  GLOBAL_LOG_OUT: async (
    { dispatch },
    { kuzzle, keycloak, showLogoutPrompt },
  ) => {
    // disconnect from kuzzle & remove local session & cookie data
    await dispatch('KUZZLE_LOG_OUT', { kuzzle })

    await dispatch('KEYCLOAK_LOG_OUT', {
      keycloak,
    })

    const redirectUri = new URL(window.location.href)

    // if we trigger logout while user is not authenticated, this will automatically follow redirect to current page,
    // causing infinite loop. => TOFIX: handle logout when no KC session is active more gracefully
    if (showLogoutPrompt) {
      await keycloak?.logout({
        redirectUri,
      })
    } else {
      // when already logged out, we can redirect user to KC login page
      await dispatch('KEYCLOAK_LOGIN', { keycloak, redirectUri })
    }

    //TODO: when hub widget disconnect works OK, we should instead redirect to hub logout URL:
    // window.replace(config.ubyHub[this.$backendName].logoutURL) ?
  },
  FETCH_CURRENT_USER: async ({ commit, dispatch }, kuzzle) => {
    let currentUser

    try {
      const user = await kuzzle.auth.getCurrentUser()
      currentUser = user
    } catch (error) {
      //TODO handle properly
      throw error
    }

    commit('SET_CURRENT_USER', currentUser)
    // Disable idle logout for screen users
    if (state.currentUser.content.profileIds.includes(specificRoles.screen)) {
      await kuzzle.query({
        controller: 'auth',
        action: 'refreshToken',
        expiresIn: sessionConfig.SCREEN_EXPIRE_TIME,
      })
    }

    let currentCustomer = null

    // if user is a global OM admin (not per customer admin)
    if (state.currentUser.content.profileIds.includes(specificRoles.admin)) {
      const customers = []
      try {
        let searchResult = await kuzzle.document.search(
          configDocuments.multiTenancyConfig,
          collections.CUSTOMERS,
          {},
          {
            // TOFIX: perf issue: it takes way too long to fetch all customers when super admin on connect // refresh page
            size: 200,
            scroll: '10s',
          },
        )
        while (searchResult) {
          searchResult.hits.forEach((hit) => {
            customers.push({
              ...hit._source,
              index: hit._source.index,
            })
          })
          searchResult = await searchResult.next()
        }
      } catch (error) {
        throw error
      }

      // flag user as global admin
      commit('SET_IS_OMNISCIENT_ADMIN', true)
      commit(
        'SET_CUSTOMERS_LIST',
        customers.sort((a, b) => (a.name > b.name ? 1 : -1)),
      )

      // find persisted customer from local storage if any, otherwise fallback to first one in result list
      currentCustomer = findPersistedCustomerIfAny(customers) ?? customers[0]
      // TODO: share with non-admin ?
      commit('SET_CURRENT_CUSTOMER', currentCustomer)
    } else {
      commit('SET_IS_OMNISCIENT_ADMIN', false)
      const request = {
        controller: 'omniscient/user',
        action: 'listIndexes',
        body: {
          userId: currentUser._id,
        },
      }
      const customers = (await kuzzle.query(request)).result
      commit('SET_CUSTOMERS_LIST', customers, [])

      // find persisted customer from local storage if any, otherwise fallback to first one in result list
      currentCustomer = findPersistedCustomerIfAny(customers) ?? customers[0]
      commit('SET_CURRENT_CUSTOMER', currentCustomer)
    }

    const requestPermission = {
      controller: 'omniscient/permissions',
      action: 'search',
      body: {
        customer: currentCustomer.index,
      },
    }

    try {
      const { result } = await kuzzle.query(requestPermission)
      const { permissions, defaultPage } = result
      commit('SET_CURRENT_USER_PERMISSIONS', permissions)
      commit('SET_CURRENT_DEFAULT_PAGE', defaultPage)
    } catch (error) {
      throw error
    }

    await dispatch('site/FETCH_SITES', kuzzle, { root: true })
    await dispatch('referential/FETCH_CURRENT_REFERENTIAL', kuzzle, {
      root: true,
    })
    await dispatch('customerConfig/FETCH_CURRENT_CUSTOMER_CONFIG', kuzzle, {
      root: true,
    })
    dispatch('userPreferences/FETCH_USER_PREFERENCES', null, {
      root: true,
    })
  },
  /**
   * responsible of querying KC tokens from hub API when user landed with a hub_token in URL
   * will also try to validate provided KC tokens against current KC & create the corresponding KC user in store.
   * if failing, will return false & let calling method handle logout & redirects.
   * @param {*}
   * @param {*}
   * @returns
   */
  FETCH_HUB_USER: async (
    { commit, dispatch },
    { hubTransmissibleId, keycloak, backendName, _redirectUri },
  ) => {
    // STORE know hub user token to be aware user is coming from hub
    commit('SET_CURRENT_HUB_USER_TRANSMISSIBLE_ID', hubTransmissibleId)

    let accessToken, refreshToken, tokenHub, hubUser

    console.debug(
      '[auth/FETCH_HUB_USER] fetching user for token...',
      hubTransmissibleId,
    )
    try {
      const fetchTokenResponse = await fetch(
        `${config.ubyHub[backendName].authApiBaseURL}/${hubTransmissibleId}/auth`,
        { method: 'GET' },
      )

      if (!fetchTokenResponse.ok) {
        throw new Error(
          `failure fetching tokens from hub API for current hub user '${hubTransmissibleId}'. status='${fetchTokenResponse.status}'`,
        )
      }

      const jsonResponse = await fetchTokenResponse.json()

      accessToken = jsonResponse.accessToken
      refreshToken = jsonResponse.refreshToken
      tokenHub = jsonResponse.tokenHub
      hubUser = jsonResponse.user

      if (!accessToken || !refreshToken || !tokenHub || !hubUser) {
        throw new Error('unable to fetch all expected tokens from hub API')
      }

      // if hubUser is valid, STORE know hub user for latter compare on user_id
      commit('SET_CURRENT_HUB_USER', hubUser)

      // STORE hub user tokens for later use
      const hubUserTokens = {
        accessToken,
        refreshToken,
        tokenHub,
      }
      commit('SET_CURRENT_HUB_USER_TOKENS', hubUserTokens)
    } catch (fetchHubTokensError) {
      console.error(
        `[auth/FETCH_HUB_USER][error] unable to fetch hub tokens for user '${hubTransmissibleId}'.`,
        fetchHubTokensError,
      )
      return false
    }

    let authResult

    authResult = await dispatch('KEYCLOAK_REFRESH_TOKEN_HUB_USER', {
      keycloak,
      refreshToken,
      tokenHub,
      accessToken,
      hubTransmissibleId,
    })

    if (authResult) {
      console.debug(
        '[auth/FETCH_HUB_USER] KC auth success for hub user, redirecting to app...',
      )
    }

    // auth failed one way or another, return false to let calling method handle failure & logout accordingly
    return authResult
  },
  KEYCLOAK_REFRESH_TOKEN_HUB_USER: async (
    {},
    { keycloak, refreshToken, idToken, accessToken, hubTransmissibleId },
  ) => {
    let authResult = false

    try {
      // HACK: force internal keycloak tokens before calling silent login to check auth state
      keycloak.refreshToken = refreshToken
      keycloak.idToken = idToken
      keycloak.token = accessToken

      // HACK simulate refresh token flow with injected tokens to try to update keycloak-js internal state...
      // -1 => force refresh in any case
      // https://www.keycloak.org/docs/latest/securing_apps/#updateToken(minValidity)
      authResult = await keycloak.updateToken(-1)

      console.debug(
        '[auth/KEYCLOAK_REFRESH_TOKEN_HUB_USER] KC authResult (refreshToken strategy)',
        authResult,
        keycloak.authenticated,
      )

      // keycloakjs.init returns true when successful, false otherwise
      if (!authResult) {
        console.error(
          `[auth/KEYCLOAK_REFRESH_TOKEN_HUB_USER][error] unable to authenticate to keycloak with provided tokens for user '${hubTransmissibleId}'.`,
        )
      }
    } catch (keycloakAuthError) {
      console.error(
        `[auth/KEYCLOAK_REFRESH_TOKEN_HUB_USER][error] unable to authenticate to keycloak with provided tokens for user '${hubTransmissibleId}'.`,
        keycloakAuthError,
      )
    }

    // return authResult state or false by default to let calling method handle failure & logout accordingly
    return authResult
  },
  FETCH_PERMISSION: async ({ commit, rootGetters }, kuzzle) => {
    if (!rootGetters['auth/currentIndex']) {
      return
    }

    const request = {
      controller: 'omniscient/permissions',
      action: 'search',
      body: {
        customer: rootGetters['auth/currentIndex'],
      },
    }
    try {
      const { result } = await kuzzle.query(request)
      const { permissions, defaultPage } = result
      commit('SET_CURRENT_USER_PERMISSIONS', permissions)
      commit('SET_CURRENT_DEFAULT_PAGE', defaultPage)
    } catch (error) {
      throw error
    }
  },
}

const mutations = {
  SET_CURRENT_USER: (state, user) => {
    state.currentUser = user
  },
  // differentiate transmissible ID vs hubTokens for safe vs hub user SET to let App.vue know faster if user is coming from hub or not
  SET_CURRENT_HUB_USER_TRANSMISSIBLE_ID: (state, hubUserTransmissibleId) => {
    state.currentHubUserTransmissibleId = hubUserTransmissibleId
    localStorage.setItem(
      hubUserTransmissibleIdLocalStorageKey,
      hubUserTransmissibleId,
    )
  },
  SET_CURRENT_HUB_USER_TOKENS: (state, hubUserTokens) => {
    state.currentHubUserTokens = hubUserTokens
    localStorage.setItem(
      hubUserTokensLocalStorageKey,
      JSON.stringify(hubUserTokens),
    )
  },
  SET_CURRENT_HUB_USER: (state, hubUser) => {
    state.currentHubUser = hubUser
    localStorage.setItem(hubUserLocalStorageKey, JSON.stringify(hubUser))
  },
  UNSET_CURRENT_USER: (state) => {
    state.currentUser = null
  },
  // clears both token & hub user from state & localStorage
  UNSET_CURRENT_HUB_USER: (state) => {
    state.currentHubUser = null
    state.currentHubUserTransmissibleId = null
    state.currentHubUserTokens = null
    localStorage.removeItem(hubUserLocalStorageKey)
    localStorage.removeItem(hubUserTransmissibleIdLocalStorageKey)
    localStorage.removeItem(hubUserTokensLocalStorageKey)
  },
  SET_CURRENT_CUSTOMER: (state, customer) => {
    // TOFIX in URL management of OM frontend: when setting the current customer, we should also ALWAYS fix URL with set customer
    // this is a problem upon first landing on app where the default  customer is picked without the URL assigned,
    // and also a problem for CI
    state.currentCustomer = customer
  },
  SET_IS_OMNISCIENT_ADMIN: (state, isOmniscientAdmin) => {
    state.isOmniscientAdmin = isOmniscientAdmin
  },
  UNSET_CURRENT_CUSTOMER: (state) => {
    state.currentCustomer = null
  },
  SET_CUSTOMERS_LIST: (state, customers) => {
    state.customers = customers
  },
  UNSET_CUSTOMERS_LIST: (state) => {
    state.customers = []
  },
  SET_CURRENT_USER_PERMISSIONS: (state, permissions) => {
    state.currentPermissions = permissions
  },
  SET_CURRENT_DEFAULT_PAGE: (state, page) => {
    state.currentDefaultPage = page
  },
}

function findPersistedCustomerIfAny(customers) {
  const persistedCustomerName = localStorage.getItem('current_customer_name')

  let foundCustomer = null
  if (persistedCustomerName && customers.length) {
    foundCustomer = customers.find((el) => el.name === persistedCustomerName)
  }

  return foundCustomer
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
